import { BhapticsContent } from "src/store/reducers/contents";
import useUser from "src/api/apis";
import { produce } from "immer";
import ContentsService from "src/services/ContentsService";
import { useQuery } from "@tanstack/react-query";
import { fetchData } from "@/utils/fetcher";

function normalize(data: BhapticsContent): BhapticsContent {
  return produce(data, (draft) => {
    draft.supportedPlatforms.forEach((c, i) => {
      c.platform = ContentsService.friendlyPlatformName(c.platform);
    });
  });
}

const useBhapticsContent = (id: string) => {
  const { token } = useUser();

  const fetchBhapticsContent = async () => {
    if (!id) return undefined;
    const url = `/sdk-api/api/v1/bhaptics-contents/${encodeURIComponent(id)}/`;
    const res = await fetch(url, {
      headers: {
        authorization: token ? "Bearer " + token?.token : "",
      },
    });

    const response = await res.json();

    if (res.ok && response.status) {
      const likeCount = Math.max(0, response.message.likeCount ?? 0);
      const dislikeCount = Math.max(0, response.message.dislikeCount ?? 0);
      return {
        ...response.message,
        likeCount,
        dislikeCount,
      } as BhapticsContent;
    }
    // response.errorMessage가 있는 경우에만 에러 던지기를 원한다면 아래 주석을 해제
    // if (response.errorMessage) throw new Error(response.errorMessage);
    return undefined;
  };

  const result = useQuery<BhapticsContent | undefined>({
    queryKey: ["bhapticsContent", id, token?.token],
    queryFn: fetchBhapticsContent,
    enabled: !!id,
  });
  const { data } = result;

  return {
    ...result,
    data: !!data ? normalize(data) : data,
  };
};

export const useRecommendedContent = (id: string) => {
  const url = `/sdk-api/api/v1/bhaptics-contents/${encodeURIComponent(id)}/recommend/?size=12&platform=web`;
  const { data, isLoading } = useQuery({
    queryKey: [url],
    queryFn: () => {
      return fetchData<BhapticsContent[]>({ url });
    },
  });

  return {
    recommended: !!data
      ? produce(data, (draft) => {
          draft.forEach((d) => {
            d.supportedPlatforms.forEach((c, i) => {
              c.platform = ContentsService.friendlyPlatformName(c.platform);
            });
          });
        })
      : data,
    isLoading,
  };
};

export default useBhapticsContent;
